import React from 'react';
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

/**
 * Generic table component.
 *
 * New props:
 *   onRowClick  (item) => void  — makes entire row clickable (cursor-pointer + hover)
 *   sortConfig  { key, direction: 'asc'|'desc' } | null
 *   onSort      (key) => void   — called when a sortable column header is clicked
 *
 * Column additions:
 *   sortable: true   — adds sort icon + click handler to that column header
 *
 * The "actions" column (key === 'actions') automatically stops click propagation
 * so Delete buttons don't also trigger onRowClick.
 */
const Table = ({
  data,
  columns,
  emptyState,
  keyField = 'id',
  className = '',
  tableClassName = '',
  theadClassName = 'bg-gray-50 dark:bg-gray-950',
  thClassName = 'px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100',
  tdClassName = 'whitespace-nowrap px-3 py-4 text-sm text-gray-600 dark:text-gray-400',
  rounded = true,
  striped = true,
  onRowClick,
  sortConfig,
  onSort,
}) => {
  if (!data || data.length === 0) {
    return (
      <div className={`text-center my-20 ${className}`}>
        {emptyState?.image && (
          <img src={emptyState.image} className="mx-auto size-12 text-gray-600 dark:text-gray-400" alt="" />
        )}
        <h3 className="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {emptyState?.title || 'No data found'}
        </h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {emptyState?.description || 'Get started by creating a new item.'}
        </p>
      </div>
    );
  }

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ChevronUpDownIcon className="ml-1 inline-block h-4 w-4 text-gray-400" aria-hidden="true" />;
    }
    return sortConfig.direction === 'asc'
      ? <ChevronUpIcon className="ml-1 inline-block h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
      : <ChevronDownIcon className="ml-1 inline-block h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />;
  };

  return (
    <div className={`flow-root ${className}`}>
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className={`overflow-hidden shadow ring-1 ring-black/5 dark:ring-white/15 ${rounded ? 'sm:rounded-lg' : ''}`}>
            <table className={`min-w-full divide-y divide-gray-300 dark:divide-gray-700 ${tableClassName}`}>
              <thead className={theadClassName}>
                <tr>
                  {columns.map((column) => {
                    const isSortable = column.sortable && onSort;
                    return (
                      <th
                        key={column.key}
                        scope="col"
                        className={`
                          ${thClassName}
                          ${column.headerClassName || ''}
                          ${column.align === 'right' ? 'text-right' : 'text-left'}
                          ${isSortable ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors' : ''}
                        `}
                        style={column.width ? { width: column.width } : {}}
                        onClick={isSortable ? () => onSort(column.key) : undefined}
                      >
                        {column.title}
                        {isSortable && getSortIcon(column.key)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className={`divide-y ${striped ? 'divide-gray-200 dark:divide-gray-800 bg-white dark:bg-black' : ''}`}>
                {data.map((item) => (
                  <tr
                    key={item[keyField]}
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                    className={onRowClick
                      ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors'
                      : ''}
                  >
                    {columns.map((column) => {
                      const isActionsCol = column.key === 'actions';

                      if (column.render) {
                        return (
                          <td
                            key={column.key}
                            onClick={isActionsCol ? (e) => e.stopPropagation() : undefined}
                            className={`
                              ${column.cellClassName || tdClassName}
                              ${column.align === 'right' ? 'text-right' : 'text-left'}
                            `}
                          >
                            {column.render(item)}
                          </td>
                        );
                      }

                      let cellContent = item[column.key];

                      if (column.formatter) {
                        cellContent = column.formatter(item[column.key], item);
                      }

                      if (column.condition) {
                        cellContent = item[column.key] ? column.trueValue : column.falseValue;
                      }

                      return (
                        <td
                          key={column.key}
                          className={`
                            ${column.cellClassName || tdClassName}
                            ${column.align === 'right' ? 'text-right' : 'text-left'}
                          `}
                        >
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Table;
